import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError, authApi } from '../lib/api';

interface Props {
  mode: 'setup' | 'login';
}

export default function AdminAuth({ mode }: Props) {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [bootstrapSecret, setBootstrapSecret] = useState('');
  const [code, setCode] = useState('');
  const [needsTotp, setNeedsTotp] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      if (mode === 'setup') {
        await authApi.setup(password, bootstrapSecret);
      } else {
        await authApi.login(password, needsTotp ? code : undefined);
      }
      navigate('/admin', { replace: true });
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught.message);
        if (caught.status === 401 && /totp/i.test(caught.message)) setNeedsTotp(true);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="grid min-h-screen place-items-center bg-[#fbfaf7] px-6 text-[#1b201e]">
      <form className="w-full max-w-sm space-y-6" onSubmit={submit}>
        <header className="space-y-2 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#14564b]">
            Sheahan Studios
          </p>
          <h1 className="display text-4xl">Proposals</h1>
          <p className="text-sm text-[#4d5550]">
            {mode === 'setup' ? 'Set up the admin password.' : 'Sign in to continue.'}
          </p>
        </header>

        <div className="space-y-4">
          {mode === 'setup' && (
            <label className="block space-y-2 text-sm font-medium">
              <span>Deployment setup secret</span>
              <input
                className="w-full rounded-xl border border-[#d8d3c8] bg-white px-4 py-3 outline-none focus:border-[#14564b] focus:ring-2 focus:ring-[#14564b]/15"
                type="password"
                value={bootstrapSecret}
                onChange={(event) => setBootstrapSecret(event.target.value)}
                autoComplete="off"
                required
              />
            </label>
          )}
          <label className="block space-y-2 text-sm font-medium">
            <span>Password</span>
            <input
              className="w-full rounded-xl border border-[#d8d3c8] bg-white px-4 py-3 outline-none focus:border-[#14564b] focus:ring-2 focus:ring-[#14564b]/15"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              minLength={mode === 'setup' ? 8 : 1}
              autoComplete={mode === 'setup' ? 'new-password' : 'current-password'}
              autoFocus
              required
            />
          </label>

          {needsTotp && (
            <label className="block space-y-2 text-sm font-medium">
              <span>Authenticator code</span>
              <input
                className="w-full rounded-xl border border-[#d8d3c8] bg-white px-4 py-3 tracking-[0.3em] outline-none focus:border-[#14564b] focus:ring-2 focus:ring-[#14564b]/15"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                value={code}
                onChange={(event) => setCode(event.target.value)}
                required
              />
            </label>
          )}
        </div>

        {error && <p className="text-sm text-[#a23b2e]">{error}</p>}

        <button
          className="w-full rounded-xl bg-[#14564b] px-4 py-3 font-semibold text-white transition hover:bg-[#0c3b33] disabled:cursor-wait disabled:opacity-60"
          type="submit"
          disabled={submitting}
        >
          {submitting ? 'Working…' : mode === 'setup' ? 'Create admin' : 'Sign in'}
        </button>
      </form>
    </main>
  );
}
